import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/shared/lib/api'

// ── Types ────────────────────────────────────────────────────

export interface TeamMember {
  id: string
  email: string
  full_name: string | null
  role: string
  provider: string
  is_active: boolean
  last_login: string | null
  created_at: string
}

export interface PendingInvite {
  id: string
  email: string
  role: string
  invited_by: string | null
  expires_at: string
  accepted_at: string | null
  created_at: string
}

// ── Queries ──────────────────────────────────────────────────

export function useTeamMembers() {
  return useQuery({
    queryKey: ['team', 'members'],
    queryFn: () => api.get('/users').then((r) => r.data.data as TeamMember[]),
  })
}

export function usePendingInvites() {
  return useQuery({
    queryKey: ['team', 'invites'],
    queryFn: () => api.get('/users/invites').then((r) => r.data.data as PendingInvite[]),
  })
}

// ── Mutations ────────────────────────────────────────────────

export function useInviteUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { email: string; role: string }) =>
      api.post('/users/invite', data).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  })
}

export function useRevokeInvite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (inviteId: string) =>
      api.delete(`/users/invites/${inviteId}`).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  })
}

export function useChangeRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      api.patch(`/users/${userId}/role`, { role }).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  })
}

export function useToggleActive() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) =>
      api.patch(`/users/${userId}/deactivate`).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  })
}

export function useDeleteUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) =>
      api.delete(`/users/${userId}`).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  })
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (data: { current_password: string; new_password: string }) =>
      api.post('/users/change-password', data).then((r) => r.data.data),
  })
}
